import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../_metronic/helpers'
import { IoIosCloudUpload } from 'react-icons/io'
import { FaFileAlt, FaEnvelope } from 'react-icons/fa'
import tosterKey from '../../localization/toster.json'
import labelKey from '../../localization/label.json'
import { useReadDraftInvoicesMutation } from '../../../services/BackgroundJobApi'
import { useUploadOCRInvoicesMutation } from '../../../services/InvoiceDataDraftApi'
import heic2any from 'heic2any'

type Props = {
    show: boolean
    handleClose: () => void
    data?: any
}

type FileType = 'image' | 'pdf' | 'msg' | 'eml' | 'heic' | 'unknown'

const AddInvoiceModal = ({ show, handleClose, data }: Props) => {
    const [uploadFile, { isLoading: uploadFileIsLoading, isSuccess: uploadFileIsSuccess, isError, error }] = useUploadOCRInvoicesMutation()
    // const [readDraftInvoices, { isLoading: readLoading }] = useReadDraftInvoicesMutation()

    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [filePreviews, setFilePreviews] = useState<(string | null)[]>([]) // Array of file previews
    const [fileTypes, setFileTypes] = useState<FileType[]>([]) // Array to keep track of file types
    const [fileNames, setFileNames] = useState<string[]>([]) // Store file names

    const [selectedFiles, setSelectedFiles] = useState<File[]>([]) // Store multiple selected files
    const [isCorpCreditCardItem, setisCorpCreditCardItem] = useState<string>('') // State for dropdown
    const [isCorpCreditCardItemError, setisCorpCreditCardItemError] = useState<string>('') // Error state for dropdown

    // Helper function to determine file type
    const getFileType = (file: File): FileType => {
        const type = file.type.toLowerCase()
        const name = file.name.toLowerCase()

        if (type === 'application/pdf' || name.endsWith('.pdf')) {
            return 'pdf'
        } else if (type === 'application/vnd.ms-outlook' || name.endsWith('.msg')) {
            return 'msg'
        } else if (type === 'message/rfc822' || name.endsWith('.eml')) {
            return 'eml'
        } else if (name.endsWith('.heic') || name.endsWith('.heif')) {
            return 'heic'
        } else if (type.startsWith('image/')) {
            return 'image'
        }
        return 'unknown'
    }

    // Helper function to get file icon
    const getFileIcon = (type: FileType) => {
        switch (type) {
            case 'pdf':
                return <FaFileAlt className="text-danger" style={{ fontSize: '48px' }} />
            case 'msg':
            case 'eml':
                return <FaEnvelope className="text-primary" style={{ fontSize: '48px' }} />
            case 'heic':
                return <FaFileAlt className="text-warning" style={{ fontSize: '48px' }} />
            default:
                return <FaFileAlt className="text-secondary" style={{ fontSize: '48px' }} />
        }
    }

    // Helper function to get file extension label
    const getFileExtensionLabel = (type: FileType) => {
        switch (type) {
            case 'pdf':
                return 'PDF'
            case 'msg':
                return 'MSG'
            case 'eml':
                return 'EML'
            case 'image':
                return 'Image'
            case 'heic':
                return 'HEIC'
            default:
                return 'File'
        }
    }

    const handleImageChangeFirst = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (files) {
            const newPreviews: (string | null)[] = []
            const newFileTypes: FileType[] = []
            const newFileNames: string[] = []
            const selectedFilesArray: File[] = Array.from(files)

            setSelectedFiles(selectedFilesArray) // Store all selected files in state

            selectedFilesArray.forEach((file, index) => {
                const fileType = getFileType(file)
                newFileTypes.push(fileType)
                newFileNames.push(file.name)

                // For images and HEIC, create a preview
                if (fileType === 'image' || fileType === 'heic') {
                    if (fileType === 'heic') {
                        // Convert HEIC to JPEG using heic2any
                        heic2any({
                            blob: file,
                            toType: 'image/jpeg',
                            quality: 0.8
                        })
                        .then((convertedBlob: any) => {
                            const reader = new FileReader()
                            reader.onload = () => {
                                newPreviews[index] = reader.result as string
                                // Check if all previews are ready
                                if (newPreviews.filter(p => p !== undefined).length === selectedFilesArray.length) {
                                    setFilePreviews(newPreviews)
                                }
                            }
                            reader.readAsDataURL(convertedBlob as Blob)
                        })
                        .catch((error: unknown) => {
                            console.error('Error converting HEIC:', error)
                            newPreviews[index] = null
                            if (newPreviews.filter(p => p !== undefined).length === selectedFilesArray.length) {
                                setFilePreviews(newPreviews)
                            }
                        })
                    } else {
                        // For regular images, use FileReader directly
                        const reader = new FileReader()
                        reader.onload = () => {
                            newPreviews[index] = reader.result as string
                            if (newPreviews.filter(p => p !== undefined).length === selectedFilesArray.length) {
                                setFilePreviews(newPreviews)
                            }
                        }
                        reader.readAsDataURL(file)
                    }
                } else {
                    // For non-image files, push null as preview
                    newPreviews[index] = null
                    if (newPreviews.filter(p => p !== undefined).length === selectedFilesArray.length) {
                        setFilePreviews(newPreviews)
                    }
                }
            })

            setFileTypes(newFileTypes)
            setFileNames(newFileNames)
        }
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        // Validate transaction type
        if (!isCorpCreditCardItem) {
            setisCorpCreditCardItemError('Please select a transaction type')
            return
        }
        setisCorpCreditCardItemError('')

        if (selectedFiles.length > 0) {
            const formData = new FormData()

            selectedFiles.forEach((file) => {
                formData.append('files', file) // Append each file to the FormData object
            })

            // You can also append transaction type value to formData if needed
            formData.append('isCorpCreditCardItem', isCorpCreditCardItem)

            const response = await uploadFile(formData)
            // const uploadedFileUrls = (response as { data?: any })?.data?.result

            // if (uploadedFileUrls) {
            //     // Assuming the response contains file IDs for each uploaded file
            //     for (const fileId of uploadedFileUrls) {
            //         await readDraftInvoices({ fileId })
            //     }
            // }
        }
    }

    const handleRemoveFile = (index: number) => {
        const updatedFiles = selectedFiles.filter((_, i) => i !== index)
        const updatedPreviews = filePreviews.filter((_, i) => i !== index)
        const updatedFileTypes = fileTypes.filter((_, i) => i !== index)
        const updatedFileNames = fileNames.filter((_, i) => i !== index)

        setSelectedFiles(updatedFiles)
        setFilePreviews(updatedPreviews)
        setFileTypes(updatedFileTypes)
        setFileNames(updatedFileNames)

        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleisCorpCreditCardItemChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setisCorpCreditCardItem(e.target.value)
        if (e.target.value) {
            setisCorpCreditCardItemError('')
        }
    }

    useEffect(() => {
        if (uploadFileIsSuccess) {
            toast.success(tosterKey.receiptAddedSuccessfully)
            handleClose()
            setFilePreviews([])
            setSelectedFiles([])
            setFileTypes([])
            setFileNames([])
            setisCorpCreditCardItem('') // Reset dropdown on success
        }
    }, [uploadFileIsSuccess])

    useEffect(() => {
        if (isError && error) {
            let errorMessage = 'Something went wrong'

            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                errorMessage = (error.data as { message: string }).message
            }
            toast.error(errorMessage)
        }
    }, [isError, error])

    const handleModalClose = () => {
        setFilePreviews([]) // Clear file previews
        setSelectedFiles([]) // Clear selected files
        setFileTypes([])
        setFileNames([])
        setisCorpCreditCardItem('') // Reset dropdown
        setisCorpCreditCardItemError('') // Reset error
        handleClose()
    }

    // Render file preview
    const renderFilePreview = (preview: string | null, fileType: FileType, fileName: string, index: number) => {
        if ((fileType === 'image' || fileType === 'heic') && preview) {
            return (
                <img
                    src={preview}
                    alt={fileName}
                    className='w-100 p-2'
                    style={{ height: '400px', objectFit: 'contain' }}
                />
            )
        } else if (fileType === 'pdf' && preview) {
            return (
                <embed
                    src={`${preview}#toolbar=0&navpanes=0&scrollbar=0`}
                    type='application/pdf'
                    className='w-100 p-2'
                    style={{ height: '400px' }}
                />
            )
        } else {
            // For MSG, EML, and other file types
            return (
                <div className="d-flex flex-column align-items-center justify-content-center p-5 border rounded-3 bg-light" style={{ height: '400px' }}>
                    <div className="mb-3">
                        {getFileIcon(fileType)}
                    </div>
                    <div className="text-center">
                        <h5 className="mb-1">{fileName}</h5>
                        <span className="badge bg-secondary">{getFileExtensionLabel(fileType)}</span>
                        <p className="text-muted mt-2 mb-0">
                            <small>File size: {(selectedFiles[index]?.size / 1024).toFixed(2)} KB</small>
                        </p>
                    </div>
                </div>
            )
        }
    }

    return (
        <Modal
            aria-hidden='true'
            dialogClassName='modal-dialog modal-md modal-dialog-centered'
            show={show}
            onHide={handleModalClose}
        >
            <form className='form w-100' onSubmit={handleSubmit}>
                <div className='modal-header'>
                    <h2 className='mb-0'>Add Receipt</h2>
                    <div className='d-flex gap-3'>
                        <span className='btn btn-light btn-sm' onClick={handleModalClose}>
                            {labelKey.cancel}
                        </span>
                        <button
                            type='submit'
                            className='btn btn-primary btn-sm'
                            disabled={uploadFileIsLoading}
                        >
                            {!uploadFileIsLoading && (
                                <span className='indicator-label'>{labelKey.submit}</span>
                            )}
                            {(uploadFileIsLoading) && (
                                <span className='indicator-progress' style={{ display: 'block' }}>
                                    {labelKey.pleaseWait}...
                                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                <div className='modal-body py-lg-10 px-lg-10'>
                    {/* Info Alert Badge */}
                    <div className='row mb-5'>
                        <div className='col-md-12'>
                            <div className='alert alert-info d-flex align-items-center p-3' role='alert'>
                                <KTSVG path='/media/icons/duotune/general/gen064.svg' className='svg-icon-2 me-3' />
                                <div>Please select a transaction type to enable receipt submission.</div>
                            </div>
                        </div>
                    </div>

                    {/* Dropdown Field */}
                    <div className='row mb-5'>
                        <div className='col-md-12'>
                            <label className='required form-label fw-bold'>Is This a Corporate Credit Card Transaction</label>
                            <select
                                className={`form-select form-select-solid ${isCorpCreditCardItemError ? 'is-invalid' : ''}`}
                                value={isCorpCreditCardItem}
                                onChange={handleisCorpCreditCardItemChange}
                            >
                                <option value=''>Please select</option>
                                <option value='4'>Yes</option>
                                <option value='1'>No</option>
                            </select>
                            {isCorpCreditCardItemError && (
                                <div className='invalid-feedback'>{isCorpCreditCardItemError}</div>
                            )}
                        </div>
                    </div>

                    {/* File Upload Section */}
                    <div className='row'>
                        <div className='col-md-12 mb-5'>
                            {!filePreviews.length && (
                                <div className='position-relative'>
                                    <div className='border border-dashed border-secondary border-4 w-100 h-100px d-flex flex-column justify-content-center align-items-center'>
                                        <IoIosCloudUpload className='text-muted' style={{ fontSize: '40px' }} />
                                        <h3 className='text-muted text-center'>Drag & Drop Images / Files Here</h3>
                                        <p className='text-muted small text-center mb-0'>Supported: PNG, JPG, JPEG, HEIC, PDF, MSG, EML</p>
                                    </div>
                                    <input
                                        type='file'
                                        id='file'
                                        name='file'
                                        accept='.png,.jpg,.jpeg,.heic,.heif,.pdf,.msg,.eml'
                                        ref={fileInputRef}
                                        onChange={handleImageChangeFirst}
                                        className='form-control form-control-solid default-input h-100px position-absolute top-0 opacity-0'
                                        multiple
                                    />
                                </div>
                            )}

                            {filePreviews.map((preview, index) => (
                                <div key={index} className='position-relative mb-3'>
                                    <div
                                        className='btn btn-icon btn-bg-light hover-danger btn-sm position-absolute m-4 '
                                        style={{ left: 10, zIndex: 12, top: '13px' }}
                                        onClick={() => handleRemoveFile(index)}
                                    >
                                        <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-hover-danger' />
                                    </div>

                                    {renderFilePreview(preview, fileTypes[index], fileNames[index], index)}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Show file count if multiple files */}
                    {selectedFiles.length > 1 && (
                        <div className="row">
                            <div className="col-md-12">
                                <div className="alert alert-primary d-flex align-items-center" role="alert">
                                    <i className="fas fa-info-circle me-2"></i>
                                    {selectedFiles.length} file(s) selected for upload
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </form>
        </Modal>
    )
}

export default AddInvoiceModal